import React, { useEffect, useRef, useState } from "react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowRight, MapPin, Play } from "lucide-react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function EliteSurfaceRestoration() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="10986" id="the-ritual-of-restoration" className="dark bg-background text-foreground relative h-screen min-h-[800px] w-full overflow-hidden flex items-center justify-center">
      {}
      <div className="absolute inset-0 z-0">
        <video autoPlay loop muted playsInline poster="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/hero_pressure_wash_foam.png" className="h-full w-full object-cover opacity-60">
          {}
          <source src="/assets/hero-background.mp4" type="video/mp4" />
        </video>
        {}
        <div className="absolute inset-0 bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1593773/img/hero_pressure_wash_foam.png)] bg-cover bg-center -z-10" />
        
        {}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(13,17,23,0.9)_100%)]" />
        
        {}
        <div className="absolute inset-0 bg-black/40" />
      </div>

      {}
      <div ref={ref} className={`relative z-10 container mx-auto px-4 flex flex-col items-center text-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
        {}
        <div className="mb-8 animate-fade-in">
          <Badge variant="outline" className="px-4 py-1.5 text-sm md:text-base border-cyan-500/30 text-cyan-400 bg-cyan-950/20 backdrop-blur-md shadow-[0_0_20px_rgba(6,182,212,0.2)] rounded-sm uppercase tracking-widest flex items-center gap-2 font-mono">Professional Mobile Auto Detailing in Glenview &amp; Nearby Areas&nbsp;</Badge>
        </div>

        {}
        <h1 className="font-serif font-black uppercase tracking-widest leading-[0.9] text-5xl md:text-7xl lg:text-[120px] mb-6 max-w-6xl">
          <span className="bg-gradient-to-b from-slate-100 via-slate-300 to-slate-500 bg-clip-text text-transparent drop-shadow-2xl">
            The Art Of
          </span>
          <br />
          <span className="bg-gradient-to-b from-slate-200 via-slate-400 to-slate-600 bg-clip-text text-transparent drop-shadow-2xl">
            The Bath
          </span>
        </h1>

        {}
        <p className="text-lg md:text-xl text-slate-400 max-w-2xl mb-10 font-light tracking-widest uppercase">
          Meticulous automotive restoration and paint protection. 
          Experience industrial luxury and high-tech precision for your vehicle.
        </p>

        {}
        <div className="flex flex-col sm:flex-row gap-6 w-full justify-center items-center">
          {}
          <Link to="/GetInTouchPage">
            <Button size="lg" className="relative overflow-hidden group bg-cyan-500 hover:bg-cyan-500 text-black border-none h-16 px-10 text-lg font-bold uppercase tracking-widest rounded-sm skew-x-[-10deg] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_40px_rgba(6,182,212,0.6)]">
              <span className="absolute inset-0 w-full h-full bg-gradient-to-r from-cyan-300 to-cyan-600 opacity-0 group-hover:opacity-100 transition-opacity duration-500 ease-out" />
              <span className="absolute inset-0 w-0 bg-white/30 transition-all duration-300 ease-out group-hover:w-full skew-x-[20deg] origin-left" />
              <span className="relative skew-x-[10deg] flex items-center gap-2">
                Get in Touch <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </Link>

          {}
          <Link to="/TreatmentsPage">
            
          </Link>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-background to-transparent z-10 pointer-events-none" />
    </section>;
}